<script setup lang="ts">
import "@recordsure/unity-scratchpad/scratchpad.css";

import {
  AlertDialogRoot,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogTrigger,
} from "reka-ui";
import { Editor, EditorContent, VueWidgetRenderer } from "@tiptap/vue-3";
import { useRouter } from "vue-router";
import { computed, inject, nextTick, onBeforeUnmount, ref, shallowRef, useTemplateRef, watch } from "vue";
import { fromPromise } from "xstate";
import { useMachine } from "@xstate/vue";

import type { ScratchpadAuthorIndex } from "@recordsure/unity-scratchpad/authors";
import {
  createEmptyScratchpad,
  type ScratchpadDocument,
  type ScratchpadSource,
} from "@recordsure/unity-scratchpad/document";
import { createScratchpadExtensions, type ScratchpadBlockMetadataProps } from "@recordsure/unity-scratchpad/extensions";
import {
  getScratchpadStatus,
  hasScratchpadError,
  isSameScratchpadStatus,
  loadScratchpad,
  loadScratchpadAuthors,
  scratchpadMachine,
  scratchpadMessages,
  writeScratchpad,
  type ScratchpadPersistence,
  type ScratchpadStatus,
} from "@recordsure/unity-scratchpad/machine";
import { rebaseScratchpad, scratchpadConflictKey, type ScratchpadChoice } from "@recordsure/unity-scratchpad/merge";

import { scratchpadService } from "@/services";
import type { ScratchpadLocation } from "@/services/ScratchpadService";
import { isApiError } from "@/services/Api";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";
import { useUserPermissionStore } from "@/stores/user-permission-store";
import {
  useCaseSelectionMenuStore,
  useSelectionMenuContribution,
  type SelectionSnapshot,
} from "@/components/SelectionMenu/case-selection-menu-store";
import BlockMetadataRow from "./BlockMetadataRow.vue";
import ScratchpadConflicts from "./ScratchpadConflicts.vue";
import ScratchpadAuthorPicker from "./ScratchpadAuthorPicker.vue";

const CSP_NONCE = inject<string>("NONCE");
const router = useRouter();

const routeParams = useCaseRouteParams();
const location = {
  client: routeParams.value.client,
  brand: routeParams.value.brand,
  casename: routeParams.value.casename,
};
const persistence: ScratchpadPersistence<ScratchpadLocation> = {
  ...scratchpadService,
  // The API client rejects a 404 as a 204, so map it back: the scratchpad doesn't exist.
  getErrorStatus: (error: unknown) => (isApiError(error) ? (error.status === 204 ? 404 : error.status) : undefined),
};

const ownerId = useUserPermissionStore().userName!.toLowerCase();

const listeners = new AbortController();
// Register before useMachine's stop hook. An active request may leave a queued final edit unsent;
// after unmount we discard responses and cannot start a follow-up with the returned concurrency.
onBeforeUnmount(() => {
  send({ type: "FLUSH" });
  listeners.abort();
});
const {
  snapshot,
  send,
  actorRef: machine,
} = useMachine(
  scratchpadMachine.provide({
    actors: {
      load: fromPromise(() => loadScratchpad(persistence, location)),
      write: fromPromise(({ input }) => writeScratchpad(persistence, location, input)),
      loadAuthors: fromPromise(() => loadScratchpadAuthors(persistence, location)),
      loadAuthor: fromPromise(({ input }) => loadScratchpad(persistence, location, input.reference)),
    },
  }),
  { input: { ownerId } },
);

const permissionStore = useUserPermissionStore();
const selectionMenuStore = useCaseSelectionMenuStore();

const context = computed(() => snapshot.value.context);
const loading = computed(() => snapshot.value.matches({ owner: "loading" }));
const loadFailed = computed(() => snapshot.value.matches({ owner: "loadFailed" }));
const readonly = computed(() => snapshot.value.hasTag("readonly"));

const conflicted = computed(() => context.value.conflict != null);
const reviewing = computed(() => snapshot.value.hasTag("review"));
const conflicts = computed(() => {
  const current = context.value;
  return current.conflict != null
    ? rebaseScratchpad(current.conflict.documentBase, current.document, current.acknowledged?.document ?? null)
        .conflicts
    : null;
});

const canWrite = computed(() => permissionStore.userPermission.writeAccess);
const canEdit = computed(() => canWrite.value && !readonly.value);

function confirm(choices: ReadonlyMap<string, ScratchpadChoice>) {
  send({ type: "SYNC", choose: (conflict) => choices.get(scratchpadConflictKey(conflict)) });
}

window.addEventListener("blur", () => send({ type: "FLUSH" }), { signal: listeners.signal });
window.addEventListener("focus", () => send({ type: "FOCUS" }), { signal: listeners.signal });
window.addEventListener(
  "beforeunload",
  (event) => {
    send({ type: "FLUSH" });
    const { context } = machine.getSnapshot();
    if (context.conflict != null || context.document !== (context.acknowledged?.document ?? null))
      event.preventDefault();
  },
  { signal: listeners.signal },
);

const autoAddedId = shallowRef<string>();

const editableDocument = computed<ScratchpadDocument, ScratchpadDocument | null>({
  get() {
    return context.value.document ?? createEmptyScratchpad();
  },
  set(document) {
    send({ type: "UPDATE", document });
  },
});

const caseSummary = computed(() => editableDocument.value.caseSummary);
const writeFailed = computed(() => context.value.writeFailed);
const refreshFailed = computed(() => context.value.refreshError != null);
const reviewKey = computed(() => context.value.acknowledged?.concurrency ?? "absent");
const busy = computed(() => snapshot.value.hasTag("busy"));
const awaitingChoices = computed(() => snapshot.value.matches({ owner: { conflict: { review: "choosing" } } }));
const refreshingReview = computed(() => snapshot.value.matches({ owner: { conflict: { review: "refreshing" } } }));

const collapsed = computed(() => context.value.collapsed);
const summaryCollapsed = ref(true);

const authorPicker = useTemplateRef<InstanceType<typeof ScratchpadAuthorPicker>>("author-picker");

// Unchanged by edits to the owner's document; `viewing` also keeps its reference through unchanged polls.
const authors = computed(() => context.value.authors);
const viewing = computed(() => context.value.viewing);
const ownerActive = computed(() => viewing.value == null);
const activeUser = computed(() => viewing.value?.identityProviderId ?? ownerId);
const viewingFailed = computed(() => snapshot.value.matches({ viewing: { other: "failed" } }));
// Nothing to show yet; a fetch behind a shown version keeps showing it.
const viewingLoading = computed(
  () => snapshot.value.matches({ viewing: { other: "fetching" } }) && viewing.value?.snapshot == null,
);
const noAuthors: ScratchpadAuthorIndex = new Map();
const authorIndex = computed(() => (authors.value.status === "loaded" ? authors.value.index : noAuthors));

const now = ref(Date.now());
const ticker = setInterval(() => (now.value = Date.now()), 60_000);
onBeforeUnmount(() => clearInterval(ticker));

const ownStatus = computed<ScratchpadStatus>((previous) => {
  const next = getScratchpadStatus(snapshot.value);
  return previous != null && isSameScratchpadStatus(previous, next) ? previous : next;
});
const ownHasError = computed(() => hasScratchpadError(snapshot.value));
const ownUpdatedAt = computed(() => context.value.acknowledged?.updated ?? null);
const currentUser = computed(() => ({
  identityProviderId: ownerId,
  status: ownStatus.value,
  updatedAt: ownUpdatedAt.value,
}));

function selectAuthor(identityProviderId: string) {
  send({ type: "AUTHOR.SELECT", identityProviderId });
}

function backToMine() {
  selectAuthor(ownerId);
  void nextTick(() => authorPicker.value?.focus());
}

const panel = useTemplateRef<HTMLElement>("panel");

// Set only by the collapse button, so the launcher doesn't take focus on load.
let focusLauncherOnMount = false;

function openPanel() {
  send({ type: "PANEL.EXPAND" });
  // The panel uses v-show, so it is already mounted; focus it once it is displayed.
  void nextTick(() => panel.value?.focus());
}

function collapsePanel() {
  focusLauncherOnMount = true;
  send({ type: "PANEL.COLLAPSE" });
}

function focusLauncher({ el }: { el: HTMLElement }) {
  if (!focusLauncherOnMount) return;
  focusLauncherOnMount = false;
  el.focus();
}

const width = ref(460);
const height = ref(520);

const preferenceKey = "unity:scratchpad:layout";

try {
  const stored = localStorage.getItem(preferenceKey);
  if (stored != null) {
    const preferences: unknown = JSON.parse(stored);
    if (typeof preferences !== "object" || preferences == null) throw new Error("Invalid Value");

    if (
      "width" in preferences &&
      Number.isFinite(preferences.width) &&
      "height" in preferences &&
      Number.isFinite(preferences.height)
    ) {
      width.value = Math.max(320, Math.min(preferences.width as number, 1000));
      height.value = Math.max(300, Math.min(preferences.height as number, 1000));
    } else {
      throw new Error("Invalid scratchpad layout preferences");
    }
  }
} catch (cause) {
  console.error("Unable to restore scratchpad layout", cause);
}

function savePreferences() {
  localStorage.setItem(preferenceKey, JSON.stringify({ width: width.value, height: height.value }));
}

const highlightColor = ref("#fff59d");

const editor = shallowRef<Editor>();

function redirectToSource(source: ScratchpadSource) {
  const params = {
    brand: routeParams.value.brand,
    casename: routeParams.value.casename,
    casefile: source.fileReference,
  };

  void router.push(
    source.kind === "document"
      ? { name: "casefilepage", params: { ...params, filepage: source.page }, force: true }
      : {
          name: "casefiletimerange",
          params: { ...params, startTime: source.startTime, endTime: source.endTime },
          force: true,
        },
  );
}

function createEditor(document = editableDocument.value, focus = false) {
  editor.value?.destroy();
  editor.value = new Editor({
    content: document.content,
    editable: canEdit.value,
    injectNonce: CSP_NONCE,
    extensions: createScratchpadExtensions({
      placeholder: "Write notes, or select text in the Record View to add a data point",
      blockMetadata: {
        onRedirectTo: redirectToSource,
        latestAddedDataPointSource: () => context.value.latestAddedDataPointSource,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        renderWidget: (options) => VueWidgetRenderer<ScratchpadBlockMetadataProps>(BlockMetadataRow, options),
      },
    }),
    editorProps: { attributes: { "aria-label": "Scratchpad content", role: "textbox", "aria-multiline": "true" } },
    onUpdate: ({ editor }) => void (editableDocument.value = { ...editableDocument.value, content: editor.getJSON() }),
    // Tiptap's focus command would add an undo step, so focus the view.
    onCreate: ({ editor }) => focus && editor.view.focus(),
  });
}
watch(
  [loading, loadFailed],
  ([isLoading, failed], _, onCleanup) => {
    if (isLoading || failed) return;
    createEditor();
    const subscription = machine.on("documentReplaced", ({ document }) => {
      createEditor(document ?? createEmptyScratchpad(), document != null && ownerActive.value);
    });
    onCleanup(() => {
      subscription.unsubscribe();
      editor.value?.destroy();
      editor.value = undefined;
    });
  },
  { immediate: true },
);
watch(canEdit, (value) => editor.value?.setEditable(value, false));

// Built in a watcher, as its widgets mount Vue components, which must not happen during render.
// Rebuilt only for another author, or another version of theirs; a refetch of the same version keeps its editor.
const otherAuthorEditor = shallowRef<Editor>();
watch(
  [() => viewing.value?.identityProviderId, () => viewing.value?.snapshot?.concurrency],
  ([identityProviderId]) => {
    const author = viewing.value?.snapshot;
    otherAuthorEditor.value?.destroy();
    otherAuthorEditor.value =
      identityProviderId == null
        ? undefined
        : new Editor({
            editable: false,
            content: author?.document.content ?? null,
            injectNonce: CSP_NONCE,
            extensions: createScratchpadExtensions({
              placeholder: author == null ? scratchpadMessages.empty : scratchpadMessages.nothingAdded,
              blockMetadata: {
                onRedirectTo: redirectToSource,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                renderWidget: (options) => VueWidgetRenderer<ScratchpadBlockMetadataProps>(BlockMetadataRow, options),
              },
            }),
            editorProps: {
              attributes: {
                "aria-label": `${identityProviderId}'s scratchpad`,
                "aria-readonly": "true",
                role: "textbox",
                "aria-multiline": "true",
              },
            },
          });
  },
  { immediate: true },
);
onBeforeUnmount(() => otherAuthorEditor.value?.destroy());

const buttons = computed(() => {
  const current = editor.value;
  if (current == null) return [];

  return [
    {
      label: "Bold",
      icon: "fa-bold",
      active: current.isActive("bold"),
      run: () => current.chain().focus().toggleBold().run(),
    },
    {
      label: "Italic",
      icon: "fa-italic",
      active: current.isActive("italic"),
      run: () => current.chain().focus().toggleItalic().run(),
    },
    {
      label: "Underline",
      icon: "fa-underline",
      active: current.isActive("underline"),
      run: () => current.chain().focus().toggleUnderline().run(),
    },
    {
      label: "Bulleted list",
      icon: "fa-list-ul",
      active: current.isActive("bulletList"),
      run: () => current.chain().focus().toggleBulletList().run(),
    },
    {
      label: "Numbered list",
      icon: "fa-list-ol",
      active: current.isActive("orderedList"),
      run: () => current.chain().focus().toggleOrderedList().run(),
    },
    {
      label: "Highlight",
      icon: "fa-highlighter",
      active: current.isActive("highlight"),
      run: () => current.chain().focus().toggleHighlight({ color: highlightColor.value }).run(),
    },
    {
      label: "Clear formatting",
      icon: "fa-eraser",
      run: () => current.chain().focus().unsetAllMarks().run(),
    },
    {
      label: "Undo",
      icon: "fa-rotate-left",
      disabled: !current.can().undo(),
      run: () => current.chain().focus().undo().run(),
    },
    {
      label: "Redo",
      icon: "fa-rotate-right",
      disabled: !current.can().redo(),
      run: () => current.chain().focus().redo().run(),
    },
  ];
});

function changeHighlightColor(event: Event) {
  if (readonly.value) return;
  if (!(event.target instanceof HTMLInputElement)) return;
  highlightColor.value = event.target.value;
  editor.value?.chain().focus().setHighlight({ color: highlightColor.value }).run();
}

function clearDocument() {
  if (readonly.value) return;
  editableDocument.value = null;
}

// Only the panel's state when a selection completes decides; expanding it later never adds that selection.
watch(
  () => selectionMenuStore.snapshot,
  (snapshot) => {
    if (snapshot == null || collapsed.value || !canEdit.value) return;

    try {
      addDataPoint(snapshot);
      autoAddedId.value = snapshot.id;
    } catch (e) {
      console.error(e);
    }
  },
);

useSelectionMenuContribution({
  id: "scratchpad",
  label: "ScratchPad",
  getActions: (snapshot) =>
    snapshot.id === autoAddedId.value || !canWrite.value
      ? []
      : [
          {
            id: "add-data-point",
            label: "Add to ScratchPad",
            icon: "fa-pen-to-square",
            disabledReason: conflicted.value
              ? "Resolve ScratchPad changes first"
              : loading.value
                ? "ScratchPad is loading"
                : loadFailed.value
                  ? "ScratchPad couldn't load"
                  : undefined,
            run: ({ snapshot }) => {
              addDataPoint(snapshot);
              send({ type: "PANEL.EXPAND" });
            },
          },
        ],
});

function addDataPoint({ source }: SelectionSnapshot) {
  const current = editor.value;
  if (!canEdit.value || conflicted.value || current == null)
    throw new Error("ScratchPad isn't ready; nothing was added");

  const doc = current.state.doc;
  const last = doc.lastChild;

  current.commands.insertContentAt(
    // Insert before the trailing empty paragraph (kept by TrailingNode) rather than after it.
    last?.type.name === "paragraph" && last.content.size === 0 ? doc.content.size - last.nodeSize : doc.content.size,
    { type: "dataPoint", attrs: { source } },
  );

  // BlockMetadataRow focuses the label when its data point's source *becomes* the latest added one. The datapoint is created above, so we set the state afterwards, so the component sees the change.
  // It also switches back to the owner's scratchpad if another author's is shown.
  send({ type: "CAPTURED", source });
}

let stopResize: (() => void) | undefined;
function startResize(event: PointerEvent) {
  if (event.button !== 0) return;

  stopResize?.();
  const start = { x: event.clientX, y: event.clientY, width: width.value, height: height.value };

  function move(event: PointerEvent) {
    width.value = Math.max(320, Math.min(window.innerWidth - 32, start.width + start.x - event.clientX));
    height.value = Math.max(300, Math.min(window.innerHeight - 32, start.height + start.y - event.clientY));
  }

  function stop() {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", stop);
    window.removeEventListener("pointercancel", stop);

    stopResize = undefined;

    savePreferences();
  }

  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", stop);
  window.addEventListener("pointercancel", stop);

  stopResize = stop;
  event.preventDefault();
}
onBeforeUnmount(() => stopResize?.());
</script>
<template>
  <p role="status" aria-live="polite" aria-atomic="true" class="tw-sr-only">
    {{
      !collapsed
        ? ""
        : loadFailed
          ? scratchpadMessages.loadFailed
          : conflicted
            ? refreshFailed
              ? scratchpadMessages.latestFailed
              : scratchpadMessages.changedElsewhere
            : writeFailed
              ? scratchpadMessages.writeFailed
              : ""
    }}
  </p>
  <button
    v-if="collapsed"
    type="button"
    class="tw-fixed tw-bottom-4 tw-right-4 tw-z-40 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-3 tw-text-black tw-shadow-lg"
    aria-label="Open scratchpad"
    @vue:mounted="focusLauncher"
    @click="openPanel"
  >
    <template v-if="loadFailed">
      <i class="fa fa-triangle-exclamation tw-mr-2 tw-text-red-700" aria-hidden="true"></i>
      {{ scratchpadMessages.loadFailed }}
    </template>
    <template v-else-if="conflicted">
      <i class="fa fa-triangle-exclamation tw-text-amber-600 tw-mr-2" aria-hidden="true"></i>
      {{ scratchpadMessages.changedElsewhere }}
    </template>
    <template v-else-if="writeFailed">
      <i class="fa fa-triangle-exclamation tw-mr-2 tw-text-red-700" aria-hidden="true"></i>
      {{ scratchpadMessages.writeFailed }}
    </template>
    <template v-else>
      <i class="fa fa-pen-to-square tw-mr-2" aria-hidden="true"></i>
      Scratchpad
    </template>
  </button>
  <section
    v-show="!collapsed"
    ref="panel"
    tabindex="0"
    aria-label="Case scratchpad"
    :aria-busy="loading"
    class="scratchpad-panel tw-fixed tw-bottom-4 tw-right-4 tw-z-40 tw-flex tw-flex-col tw-divide-y tw-overflow-hidden tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-text-black tw-shadow-xl"
    :style="{ width: `${width}px`, height: `${height}px` }"
  >
    <header class="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-px-3 tw-py-1">
      <div class="tw-flex tw-items-center tw-gap-2">
        <button
          type="button"
          class="scratchpad-resize"
          aria-label="Resize scratchpad"
          title="Drag to resize; use arrow keys for keyboard resizing"
          @pointerdown="startResize"
          @keydown.left.prevent="
            width = Math.min(width + 20, 1000);
            savePreferences();
          "
          @keydown.right.prevent="
            width = Math.max(width - 20, 320);
            savePreferences();
          "
          @keydown.up.prevent="
            height = Math.min(height + 20, 1000);
            savePreferences();
          "
          @keydown.down.prevent="
            height = Math.max(height - 20, 300);
            savePreferences();
          "
        >
          <i class="fa fa-up-right-and-down-left-from-center tw-rotate-90" aria-hidden="true"></i>
        </button>
        <h2 class="tw-m-0 tw-text-sm tw-font-semibold tw-leading-5">Scratchpad</h2>
      </div>
      <button
        type="button"
        aria-label="Collapse scratchpad"
        class="tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded tw-text-xs hover:tw-bg-gray-100"
        @click="collapsePanel"
      >
        <i class="fa fa-minus" aria-hidden="true"></i>
      </button>
    </header>
    <div
      v-if="canWrite && buttons.length > 0"
      v-show="ownerActive"
      role="toolbar"
      aria-label="Scratchpad formatting"
      class="tw-bg-gray-50 tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-0.5 tw-gap-y-1 tw-px-2 tw-py-1"
    >
      <template v-for="button in buttons" :key="button.label">
        <button
          type="button"
          :title="button.label"
          :aria-label="button.label"
          :aria-pressed="button.active"
          :disabled="readonly || button.disabled"
          class="tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded tw-text-xs hover:tw-bg-gray-200 disabled:tw-opacity-40"
          :class="{ 'tw-bg-blue-100': button.active }"
          @mousedown.prevent
          @click="button.run"
        >
          <i class="fa" :class="button.icon" aria-hidden="true"></i>
        </button>
        <label
          v-if="button.label === 'Highlight'"
          class="tw-flex tw-size-7 tw-items-center tw-justify-center"
          title="Highlight colour"
        >
          <input
            type="color"
            :value="highlightColor"
            aria-label="Highlight colour"
            :disabled="readonly"
            class="tw-size-5 tw-cursor-pointer"
            @input="changeHighlightColor"
          />
        </label>
      </template>
    </div>
    <div class="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      <div :inert="readonly || !ownerActive || undefined" class="tw-min-h-0 tw-flex-1 tw-overflow-auto tw-p-2">
        <EditorContent :editor="editor" class="scratchpad-editor" />
      </div>
      <div v-show="ownerActive" class="tw-contents">
        <div
          v-if="loading"
          role="status"
          class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/70 tw-backdrop-blur-sm"
        >
          <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
          <span class="tw-sr-only">Loading scratchpad</span>
        </div>
        <div
          v-else-if="loadFailed"
          role="alert"
          class="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-bg-white/90 tw-text-xs tw-backdrop-blur-sm"
        >
          {{ scratchpadMessages.loadFailed }}
          <button type="button" class="tw-btn-md-gray" @click="send({ type: 'RETRY' })">Retry</button>
        </div>
        <ScratchpadConflicts
          v-else-if="canWrite && reviewing && conflicts != null"
          :key="reviewKey"
          :conflicts
          :disabled="!awaitingChoices"
          :aria-busy="busy"
          @confirm="confirm"
        >
          <p v-if="busy" role="status" class="tw-m-0">
            {{ refreshingReview ? "Loading latest changes..." : "Saving changes..." }}
          </p>
          <p v-if="writeFailed" role="alert" class="tw-m-0 tw-text-red-700">{{ scratchpadMessages.writeFailed }}</p>
          <p v-if="refreshFailed" role="alert" class="tw-m-0 tw-text-red-700">
            {{ scratchpadMessages.latestFailed }}
            <button type="button" class="tw-btn-md-gray" @click="send({ type: 'SYNC' })">Retry</button>
          </p>
        </ScratchpadConflicts>
      </div>
      <div
        v-if="viewing != null"
        :key="viewing.identityProviderId"
        class="tw-absolute tw-inset-0 tw-overflow-auto tw-bg-white tw-p-2"
      >
        <div
          v-if="viewingFailed"
          role="alert"
          class="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-text-xs"
        >
          {{ scratchpadMessages.authorFailed(viewing.identityProviderId) }}
          <button type="button" class="tw-btn-md-gray" @click="send({ type: 'AUTHOR.RETRY' })">Retry</button>
        </div>
        <div v-else-if="viewingLoading" role="status" class="tw-flex tw-h-full tw-items-center tw-justify-center">
          <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
          <span class="tw-sr-only">Loading {{ viewing.identityProviderId }}'s scratchpad</span>
        </div>
        <EditorContent v-else :editor="otherAuthorEditor" class="scratchpad-editor" />
      </div>
    </div>
    <div class="tw-shrink-0 tw-px-3">
      <button
        type="button"
        class="hover:tw-bg-gray-50 tw-flex tw-w-full tw-items-center tw-justify-between tw-py-2 tw-text-xs tw-font-semibold tw-leading-4"
        :aria-expanded="!summaryCollapsed"
        @click="summaryCollapsed = !summaryCollapsed"
      >
        Case summary
        <i class="fa" :class="summaryCollapsed ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i>
      </button>
      <textarea
        v-if="!summaryCollapsed"
        v-show="ownerActive"
        :value="caseSummary"
        aria-label="Case summary"
        :disabled="readonly"
        :readonly="!canWrite"
        :placeholder="scratchpadMessages.caseSummaryPlaceholder"
        class="tw-max-h-40 tw-w-full tw-rounded tw-border tw-p-2 tw-text-xs [field-sizing:content]"
        @change="
          (event) =>
            (editableDocument = {
              ...editableDocument,
              caseSummary: (event.target as HTMLTextAreaElement).value.trim(),
            })
        "
      />
      <p
        v-if="!summaryCollapsed && viewing != null && !viewingFailed && !viewingLoading"
        class="tw-m-0 tw-max-h-40 tw-overflow-auto tw-whitespace-pre-wrap tw-pb-2 tw-text-xs"
        :class="{ 'tw-italic tw-text-gray-500': !viewing.snapshot?.document.caseSummary }"
      >
        {{
          viewing.snapshot?.document.caseSummary ||
          (viewing.snapshot == null ? scratchpadMessages.empty : scratchpadMessages.nothingAdded)
        }}
      </p>
    </div>
    <footer class="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-1.5">
      <ScratchpadAuthorPicker
        v-if="!collapsed"
        ref="author-picker"
        :active="activeUser"
        :current-user
        :authors="authorIndex"
        :authors-status="authors.status"
        :now
        @select="selectAuthor"
      />
      <div class="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
        <template v-if="ownerActive">
          <button
            v-if="canWrite && conflicted && !reviewing"
            type="button"
            class="tw-btn-md-gray tw-shrink-0"
            @click="send({ type: 'SYNC' })"
          >
            Review
          </button>
          <AlertDialogRoot>
            <AlertDialogTrigger
              v-if="canWrite"
              title="Clear scratchpad"
              aria-label="Clear scratchpad"
              :disabled="readonly"
              class="tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded tw-text-xs hover:tw-bg-gray-100"
            >
              <i class="fa fa-trash-can" aria-hidden="true"></i>
            </AlertDialogTrigger>
            <AlertDialogPortal>
              <AlertDialogOverlay class="tw-fixed tw-inset-0 tw-z-[1000000] tw-bg-black/30" />
              <AlertDialogContent
                class="tw-fixed tw-left-1/2 tw-top-1/2 tw-z-[1000001] tw-w-80 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-lg tw-bg-white tw-p-5 tw-text-black tw-shadow-xl"
              >
                <AlertDialogTitle class="tw-text-lg tw-font-semibold">Clear scratchpad?</AlertDialogTitle>
                <AlertDialogDescription class="tw-mt-2 tw-text-sm">
                  This permanently deletes all content and the case summary from your scratchpad for this case.
                </AlertDialogDescription>
                <div class="tw-mt-4 tw-flex tw-justify-end tw-gap-2">
                  <AlertDialogCancel class="tw-btn-md-gray">Cancel</AlertDialogCancel>
                  <AlertDialogAction class="tw-btn-md-red" @click="clearDocument"> Clear scratchpad </AlertDialogAction>
                </div>
              </AlertDialogContent>
            </AlertDialogPortal>
          </AlertDialogRoot>
        </template>
        <button
          v-else
          type="button"
          :aria-label="ownHasError ? 'Back to mine (your scratchpad has an error)' : 'Back to mine'"
          class="tw-relative tw-inline-flex tw-h-6 tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-md tw-px-2 tw-text-xs hover:tw-bg-gray-100"
          @click="backToMine"
        >
          <i class="fa fa-reply" aria-hidden="true"></i>
          Back to mine
          <span
            v-if="ownHasError"
            data-attention
            class="tw-absolute tw-right-0 tw-top-0 tw-size-2 tw-rounded-full tw-bg-red-600"
            aria-hidden="true"
          ></span>
        </button>
      </div>
      <p role="status" aria-live="polite" class="tw-sr-only">
        {{ ownerActive && (ownStatus.kind === "conflict" || ownStatus.kind === "failed") ? ownStatus.message : "" }}
      </p>
    </footer>
  </section>
</template>
<style scoped>
.scratchpad-panel {
  max-width: calc(100vw - 2rem);
  max-height: calc(100dvh - 2rem);
}
.scratchpad-resize {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 0.25rem;
  color: #6b7280;
  font-size: 12px;
  cursor: nwse-resize;
  touch-action: none;
}
.scratchpad-resize:hover {
  background: #f3f4f6;
}
.scratchpad-resize:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}
</style>
